import { HttpError, ValidationErrors } from "@refinedev/core";
import axios, { AxiosError, AxiosResponse } from "axios";
import { liveClient } from "client/live.client";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import { User } from "types/props";

export const apiUrl = process.env.REACT_APP_API_ENDPOINT as string;

export const AUTH_URLS = ["login", "signup", "forgot-password"];

// do not use directly, use data provider instead
const apiHelper = axios.create({
  baseURL: apiUrl,
  withCredentials: true
});

// add auth to request
apiHelper.interceptors.request.use(async (request) => {
  const authorization = await AuthManager.getAuthorization();
  if (authorization) {
    request.headers.set("Authorization", authorization);
  }
  return request;
});

// add auth issue to signup and login and refresh
apiHelper.interceptors.request.use(async (request) => {
  if (request.url && /(^|\/)auth\/([^/]+\/verify|refresh)$/.test(request.url)) {
    request.data ??= {};
    (request.data as Record<string, unknown>).issue = "cookie";
  }
  return request;
});

// get auth from response
apiHelper.interceptors.response.use(async (response: AxiosResponse<Auth>) => {
  if (
    response.config.url &&
    /(^|\/)auth\/(login|refresh)$/.test(response.config.url) &&
    response.data &&
    "accessToken" in response.data.data
  ) {
    await AuthStore.set(response.data as Auth);
  }
  return response;
});

// connect live client if already logged in
apiHelper.interceptors.response.use(async (response) => {
  if (
    response.config.url?.endsWith("/user") &&
    (response.status === 200 || response.status === 201)
  ) {
    // connect live client
    liveClient.connect();
  }
  return response;
});

// clear auth on logout
apiHelper.interceptors.response.use(async (response) => {
  if (response.config.url && /(^|\/)auth\/logout/.test(response.config.url)) {
    await AuthStore.clear();
    liveClient.disconnect();
  }
  return response;
});

// add locale to request header
apiHelper.interceptors.request.use((request) => {
  const locale = parseCookies()["locale"];
  if (locale) {
    request.headers.set("locale", locale);
  }
  return request;
});

// retry unauthorized requests after refreshing credentials
apiHelper.interceptors.response.use(
  (response) => response,
  (error) => AuthManager.handleError(error)
);

declare module "axios" {
  interface AxiosRequestConfig {
    authManagerSkip?: boolean;
  }
}

export type Auth = {
  data: User & { accessToken: string };
  message: string;
  statusCode: number;
};

export class AuthStore {
  static logTag = `[${AuthStore.name}]`;

  // use `QUIX_TOKEN_2` as cookie name instead of `QUIX_TOKEN` to not bypass CSRF protection
  // until the backend stops considering access tokens in cookies altogether
  static _quixToken2 = "QUIX_TOKEN_2";

  static async set(auth: Auth) {
    setCookie(null, this._quixToken2, auth.data.accessToken, {
      // expires: new Date(auth.accessTokenExpiresAt),
      // path: "/"
    });
  }

  static async clear() {
    destroyCookie(null, this._quixToken2);
  }

  static async get() {
    // return accessToken from saved cookies
    return parseCookies()[this._quixToken2];
  }
}

/** custom error handler for auth refresh */
export class AuthManager {
  static logTag = "[AuthManager]";
  static refreshAttemptsMax = 3; // FIXME: currently refresh logic is disabled
  static refreshing: Promise<void> | undefined;

  static async refresh() {
    await apiHelper.post("auth/refresh", {}, { authManagerSkip: true });
  }

  static async check() {
    return await apiHelper
      .get("user/me", { authManagerSkip: true })
      .then(() => true)
      .catch(() => false);
  }

  static handleError(error: unknown) {
    /**
     * ### Status Codes
     * Treat all 401 responses as unauthenticated, the api must return 403 when a user is authenticated,
     * but not authorized to access the resource.
     *
     * ### Race Condition
     * Attempt refresh multiple times to account for the race condition where failed requests
     * across multiple browser tabs could initiate refresh when one is already in progress.
     */
    if (
      error instanceof AxiosError &&
      error.response?.status === 401 &&
      !error.config?.authManagerSkip &&
      !AUTH_URLS.some((it) => error.config?.url?.includes(it))
    ) {
      if (!this.refreshing) {
        this.refreshing = (async () => {
          for (let attempt = 1; attempt <= this.refreshAttemptsMax; attempt++) {
            try {
              console.log(this.logTag, `refreshing credentials attempt: ${attempt}`);

              await this.refresh();
              break; // refresh success
            } catch (refreshError) {
              console.error(this.logTag, "Error refreshing credentials", refreshError);

              if (attempt === this.refreshAttemptsMax) {
                console.log(this.logTag, "refreshing credentials: max attempts reached");
                throw error; // max attempts reached
              }

              // check if another tab has already refreshed credentials
              if (await this.check()) {
                console.log(this.logTag, "refreshing credentials: already authenticated");
                break;
              }
            }
          }
        })();
      }
      // retry request after refreshing
      return this.refreshing
        .then(() => apiHelper({ ...error.config, authManagerSkip: true }))
        .finally(() => (this.refreshing = undefined));
    } else {
      throw error;
    }
  }

  static async getAuthorization() {
    return AuthStore.get().then((it) => (it ? `Bearer ${it}` : undefined));
  }
}

// transform axios error to refine http error
// see https://refine.dev/docs/tutorial/understanding-dataprovider/create-dataprovider/#error-handling
apiHelper.interceptors.response.use(
  (response) => response,
  (error) => {
    return error.response;
  }
  // FIXME: this needs to be enabled when the direct api calls are replaced by hooks
  // and remove the above error handling
  // (error) => Promise.reject(APIError.from(error))
);

export type APIErrorRaw = {
  message: string;
  statusCode: number;
  validationErrors?: APIValidationErrorRaw[];
};

export type APIValidationErrorRaw = {
  property: string;
  message: string;
};

/** custom error that implements refine's http error */
export class APIError extends Error implements HttpError {
  errors?: ValidationErrors | undefined;

  private constructor(
    readonly message: string,
    readonly statusCode: number,
    options?: ErrorOptions
  ) {
    super(message, options);
  }

  static from<T extends Record<string, unknown>>(error: unknown, data?: T) {
    let instance: APIError;
    if (error instanceof APIError) {
      instance = new this(error.message, error.statusCode, { cause: error.cause });
      instance = Object.assign(instance, error);
    } else if (error instanceof AxiosError) {
      const axiosError = error as AxiosError<APIErrorRaw | undefined>;
      instance = new this(
        axiosError.response?.data?.message ?? "",
        axiosError.response?.status ?? 500,
        {
          cause: error
        }
      );
      instance.errors = axiosError.response?.data?.validationErrors
        ? Object.fromEntries(
            axiosError.response.data.validationErrors.map((validationError) => [
              validationError.property,
              validationError.message
            ])
          )
        : undefined;
    } else if (error instanceof Error) {
      instance = new this(error.message, 500, { cause: error });
    } else {
      instance = new this(String(error), 500, { cause: error });
    }

    return Object.assign(instance, data);
  }
}

export default apiHelper;
